import React, { useState, useEffect, useCallback, useRef } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { authFetch } from "../lib/api";
import { pushNotification } from "../components/Topbar";
import { Icons } from "../components/icons";
import { Info, User } from "lucide-react";

const useRole = () => useSelector((s) => s.auth.user?.role || "employee");

const formatShort = (dateStr) => {
  if (!dateStr) return "—";
  const d = toLocalDate(dateStr);
  if (Number.isNaN(d.getTime())) return String(dateStr);
  return d.toLocaleDateString("en-US", { month: "short", day: "2-digit" });
};

const formatActivityDates = (a) => {
  if (!a) return null;
  if (
    ["leave_applied", "leave_approved", "leave_rejected"].includes(a.type) &&
    (a.from || a.to)
  ) {
    if (a.from && a.to && a.from !== a.to) return `${a.from} – ${a.to}`;
    return a.from || a.to;
  }
  if (["holiday_declared", "holiday_added"].includes(a.type) && a.date) {
    return a.date;
  }
  // Fallback: metadata may already hold display strings
  if (a.metadata?.from || a.metadata?.to) {
    const from = a.metadata.from;
    const to = a.metadata.to;
    if (from && to && from !== to) return `${from} – ${to}`;
    return from || to;
  }
  if (a.metadata?.date) return a.metadata.date;
  return null;
};

// Local YYYY-MM-DD — avoids UTC day-shift from Date.toISOString()
const toLocalYmd = (input) => {
  const d = toLocalDate(input);
  if (Number.isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const toLocalDate = (input) => {
  if (input == null || input === "") return new Date(NaN);
  if (input instanceof Date && !Number.isNaN(input.getTime())) {
    return new Date(input.getFullYear(), input.getMonth(), input.getDate());
  }
  const raw = String(input).trim();
  const ymd = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (ymd) {
    return new Date(Number(ymd[1]), Number(ymd[2]) - 1, Number(ymd[3]));
  }
  const d = new Date(input);
  if (Number.isNaN(d.getTime())) return d;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
};

// Sundays + only the 1st and 3rd Saturday of each month are non-working.
// 2nd / 4th / 5th Saturdays are working days (leave can be applied).
const isNonWorkingDay = (date) => {
  const d = toLocalDate(date);
  if (Number.isNaN(d.getTime())) return false;
  const day = d.getDay(); // 0 = Sunday, 6 = Saturday
  if (day === 0) return true;
  if (day === 6) {
    const nthSaturday = Math.ceil(d.getDate() / 7);
    return nthSaturday === 1 || nthSaturday === 3;
  }
  return false;
};

const timeAgo = (dateStr) => {
  if (!dateStr) return "";
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} mins ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hour${hrs > 1 ? "s" : ""} ago`;
  return `${Math.floor(hrs / 24)} days ago`;
};

// ─── Donut Chart ───────────────────────────────────────────────────────────
const DonutChart = ({ used, total, color }) => {
  const radius = 45;
  const circ = 2 * Math.PI * radius;
  const safe = total || 1;
  const filled = (used / safe) * circ;
  return (
    <svg viewBox="0 0 120 120" className="w-full max-w-[280px] max-h-[250px]">
      <circle
        cx="60"
        cy="60"
        r={radius}
        fill="none"
        stroke="#D9D9D9   "
        strokeWidth="8"
      />
      <circle
        cx="60"
        cy="60"
        r={radius}
        fill="none"
        stroke={color}
        strokeWidth="8"
        strokeDasharray={`${filled} ${circ - filled}`}
        strokeLinecap="round"
        transform="rotate(-90 60 60)"
        color="#D9D9D9"
      />
      <text
        x="60"
        y="55"
        textAnchor="middle"
        fontSize="16px"
        fontWeight="400"
        fill="#111827"
        classname="font-regular"
      >
        {used}/{total}
      </text>
      <text x="60" y="70" textAnchor="middle" fontSize="8px" fill="#9ca3af">
        Days Used
      </text>
    </svg>
  );
};

// ─── Status Badge ──────────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const map = {
    APPROVED: { bg: "#dcfce7", color: "#16a34a" },
    PENDING: { bg: "#fef9c3", color: "#ca8a04" },
    REJECTED: { bg: "#fee2e2", color: "#dc2626" },
  };
  const s = map[status] || map.PENDING;
  return (
    <span
      style={{
        background: s.bg,
        color: s.color,
        fontSize: 10,
        fontWeight: 700,
        padding: "2px 10px",
        borderRadius: 20,
        whiteSpace: "nowrap",
      }}
    >
      {status}
    </span>
  );
};

// ─── Apply Leave Modal ─────────────────────────────────────────────────────
const ApplyLeaveModal = ({ onClose, onSuccess, prefillDate }) => {
  const today = new Date();
  const [form, setForm] = useState({
    from: prefillDate || "",
    to: prefillDate || "",
    type: "ML",
    reason: "",
  });
  const [error, setError] = useState("");
  const [submitting, setSubmit] = useState(false);
  const [showTypeDropdown, setShowTypeDropdown] = useState(false);
  const fromDateRef = useRef(null);
  const toDateRef = useRef(null);

  const typeLabels = { ML: "ML", CL: "CL", RL: "RL", Emergency: "Emergency" };
  const typeDesc = {
    RL: "RL = Restricted Leave",
    CL: "CL = Casual Leave",
    ML: "ML = Medical Leave",
  };

  const handleSubmit = async () => {
    setError("");
    if (!form.from || !form.to || !form.reason.trim()) {
      setError("Please fill all fields.");
      return;
    }
    if (isNonWorkingDay(form.from) || isNonWorkingDay(form.to)) {
      setError(
        "Non-working days (Sundays, 1st & 3rd Saturdays) cannot be selected.",
      );
      return;
    }
    setSubmit(true);
    try {
      const res = await authFetch("/api/leaves/apply", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          fromDate: form.from,
          toDate: form.to,
          leaveType: form.type,
          reason: form.reason,
        }),
      });
      const json = await res.json();
      if (res.ok && json?.data) {
        onSuccess(json.data);
        pushNotification({
          title: "Leave request submitted successfully",
          lines: [
            `Type: ${form.type}`,
            `From: ${toLocalDate(form.from).toLocaleDateString()} To: ${toLocalDate(form.to).toLocaleDateString()}`,
          ],
        });
        onClose();
      } else setError(json?.message || "Failed to apply leave.");
    } catch {
      setError("Network error.");
    } finally {
      setSubmit(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
      <style>{`
        .react-datepicker-popper{z-index:9999!important}
        .react-datepicker-wrapper{width:100%}
        /* Only 1st/3rd Sat + Sundays are disabled — don't grey all weekend cells */
        .react-datepicker__day--weekend:not(.react-datepicker__day--disabled){
          color:#111827;
        }
        .react-datepicker__day--disabled.leave-non-working-day,
        .react-datepicker__day--disabled{
          color:#9ca3af !important;
          background:#f3f4f6 !important;
        }
      `}</style>
      <div className="bg-white rounded-[12px] w-full max-w-sm shadow-2xl p-6">
        <div className="flex items-center justify-between mb-1">
          <div>
            <h2 className="text-base font-medium text-[20px] text-gray-900">
              Apply Leave
            </h2>
            {(form.from || form.to) && (
              <p className="text-xs text-blue-500 mt-0.5">
                {form.from
                  ? toLocalDate(form.from).toLocaleDateString("en-GB", {
                      day: "2-digit",
                      month: "2-digit",
                      year: "numeric",
                    })
                  : "—"}
                {" → "}
                {form.to
                  ? toLocalDate(form.to).toLocaleDateString("en-GB", {
                      day: "2-digit",
                      month: "2-digit",
                      year: "numeric",
                    })
                  : "—"}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
          >
            ×
          </button>
        </div>
        <p className="text-xs text-gray-400 mb-5">
          Select period and provide details
        </p>
        {error && <p className="text-xs text-red-500 mb-3">{error}</p>}

        {/* Date row */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          {[
            { label: "From Date", key: "from", ref: fromDateRef },
            { label: "End", key: "to", ref: toDateRef },
          ].map(({ label, key, ref }) => (
            <div key={key}>
              <label className="text-xs font-medium text-gray-600 mb-1 block">
                {label}
              </label>
              <div className="relative">
                <div
                  className="absolute left-0 top-0 h-10 w-10 flex items-center justify-center rounded-l-[8px] bg-[#0404040A] border border-gray-300 border-r-0 cursor-pointer z-10"
                  onClick={() => ref.current?.setFocus()}
                >
                  <Icons.Calendar />
                </div>
                <DatePicker
                  ref={ref}
                  selected={form[key] ? toLocalDate(form[key]) : null}
                  onChange={(d) =>
                    setForm({
                      ...form,
                      [key]: d ? toLocalYmd(d) : "",
                      ...(key === "from" ? { to: "" } : {}),
                    })
                  }
                  className="w-full h-10 border border-gray-300 rounded-[8px] pl-11 pr-3 text-xs"
                  dateFormat="MMM dd, yyyy"
                  placeholderText="Apr 16, 2025"
                  minDate={
                    key === "to" && form.from ? toLocalDate(form.from) : today
                  }
                  filterDate={(d) => !isNonWorkingDay(d)}
                  dayClassName={(d) =>
                    isNonWorkingDay(d) ? "leave-non-working-day" : undefined
                  }
                  popperPlacement="bottom-start"
                  popperClassName="z-[60]"
                  showMonthDropdown
                  showYearDropdown
                  dropdownMode="select"
                  autoComplete="off"
                />
              </div>
            </div>
          ))}
        </div>

        {/* Leave Type */}
        <div className="mb-4">
          {/* Label and icon share one 16px line box (text-xs + leading-4).
              `block` removes the inline-SVG baseline gap. The 1.5px nudge
              centres the icon on the *letterforms* rather than the line box:
              in Poppins (cap height 700/1000, ascender 1050) 12px text in a
              16px line puts the baseline at 12.2px and cap-top at 3.8px, so
              the glyphs centre at 8.0px, while an unshifted 13px icon centres
              at 6.5px. Flex `items-center` cannot see this — it aligns boxes,
              not glyphs, which is why the icon read as raised. */}
          <div className="flex items-center gap-1 mb-1">
            <label className="text-xs font-medium text-gray-600 leading-4">
              Leave Type
            </label>
            <div className="relative group leading-4">
              <Info
                size={13}
                strokeWidth={2}
                className="block translate-y-[4px] text-gray-400 cursor-pointer"
              />
              <div className="hidden group-hover:block absolute left-5 top-0 bg-white border border-gray-200 rounded-xl shadow-lg z-50 p-3 w-44 text-xs text-gray-700">
                {Object.values(typeDesc).map((d) => (
                  <p key={d} className="mb-0.5">
                    {d}
                  </p>
                ))}
              </div>
            </div>
          </div>
          <div className="relative">
            <button
              type="button"
              onClick={() => setShowTypeDropdown((p) => !p)}
              className="w-full h-10 border border-gray-200 rounded-lg px-3 flex items-center justify-between text-sm text-gray-700 bg-white"
            >
              <span>{typeLabels[form.type] || form.type}</span>
              <span className="text-gray-400 text-xs">▾</span>
            </button>
            {showTypeDropdown && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setShowTypeDropdown(false)}
                />
                <div className="absolute left-0 top-11 w-full bg-white border border-gray-200 rounded-lg shadow-lg z-20 overflow-hidden">
                  {Object.keys(typeLabels).map((t) => (
                    <label
                      key={t}
                      className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-gray-50"
                    >
                      <input
                        type="radio"
                        name="leaveType"
                        value={t}
                        checked={form.type === t}
                        onChange={() => {
                          setForm({ ...form, type: t });
                          setShowTypeDropdown(false);
                        }}
                        className="accent-blue-600 w-3.5 h-3.5"
                      />
                      <span className="text-sm text-gray-700">{t}</span>
                    </label>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Reason */}
        <div className="mb-5">
          <label className="text-xs font-medium text-gray-600 mb-1 block">
            Reason
          </label>
          <textarea
            className="w-full border border-gray-200 rounded-lg p-3 text-sm resize-none outline-none focus:border-blue-400 text-gray-700"
            placeholder="Enter your reason here.."
            rows={3}
            maxLength={150}
            value={form.reason}
            onChange={(e) => setForm({ ...form, reason: e.target.value })}
          />
          <p className="text-right text-xs text-gray-400 mt-0.5">
            {form.reason.length}/150
          </p>
        </div>

        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 border border-[#4379FE] rounded-xl py-2.5 text-sm text-[#4379FE] hover:bg-gray-50 font-medium"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="flex-1 bg-[#4253C7] text-white rounded-xl py-2.5 text-sm font-normal hover:bg-blue-700 disabled:opacity-60"
          >
            {submitting ? "Applying..." : "Apply Leave"}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Declare Holiday Modal ─────────────────────────────────────────────────
const DeclareHolidayModal = ({ onClose, onDeclared }) => {
  const [form, setForm] = useState({ date: "", reason: "" });
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const holidayDateRef = useRef(null);

  const handleDeclare = async () => {
    setError("");
    if (!form.date) {
      setError("*Date is required.");
      return;
    }
    if (!form.reason.trim()) {
      setError("*Reason is required.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await authFetch("/api/holidays/declare", {
        method: "POST",
        body: JSON.stringify({
          date: form.date,
          reason: form.reason.trim(),
          name: "Emergency Holiday",
        }),
      });
      const json = await res.json();
      if (!res.ok || json?.success === false) {
        setError(json?.message || "Failed to declare holiday.");
        return;
      }
      pushNotification({
        title: "Emergency holiday declared",
        lines: [
          `Reason: ${form.reason.trim()}`,
          `Date: ${toLocalDate(form.date).toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          })}`,
        ],
      });
      onDeclared?.({ date: form.date, reason: form.reason.trim() });
      onClose();
    } catch {
      setError("Network error — holiday not declared.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50 p-4">
      {/* Design size is 677×489. Both are caps rather than fixed values so the
          card shrinks on small screens instead of overflowing the viewport:
          width falls back to 100% of the available space, and the height is a
          minimum that can grow if the content (e.g. a validation error) needs
          it, bounded by the viewport via max-h + internal scroll. */}
      <div
        className="bg-white rounded-[12px] shadow-xl w-full flex flex-col overflow-y-auto p-5 sm:p-6"
        style={{
          maxWidth: 677,
          minHeight: 489,
          maxHeight: "calc(100vh - 2rem)",
        }}
      >
        <div className="flex items-center justify-between mb-6 shrink-0">
          <h2 className="text-base text-gray-900 text-[20px] font-semibold">
            Declare Emergency Holiday
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-xl leading-none"
          >
            ×
          </button>
        </div>
        <label className="text-sm font-medium text-gray-700 mb-2 block">
          Date
        </label>
        <div className="relative mb-5 w-full shrink-0">
          <div
            className="absolute left-0 top-0 h-full w-10 flex items-center justify-center rounded-l-xl border border-gray-200 border-r-0 cursor-pointer z-10"
            onClick={() => holidayDateRef.current.setFocus()}
          >
            <Icons.Calendar className="text-blue-500" />
          </div>
          <DatePicker
            ref={holidayDateRef}
            selected={form.date ? toLocalDate(form.date) : null}
            onChange={(date) =>
              setForm({ ...form, date: date ? toLocalYmd(date) : "" })
            }
            placeholderText="Select date"
            dateFormat="dd MMM yyyy"
            className="w-full h-10 border border-gray-200 rounded-xl pl-14 pr-3 text-sm text-gray-600 outline-none"
            wrapperClassName="w-full block"
          />
        </div>
        <label className="text-sm font-medium text-gray-700 mb-2 block">
          Reason
        </label>
        {/* shrink-0 keeps the box at its intended height: as a flex item in the
            taller card it would otherwise stretch, or be squeezed when the
            viewport is short. */}
        <textarea
          className="w-full shrink-0 border border-gray-200 rounded-xl text-sm resize-none outline-none mb-6 p-3 h-24 text-gray-600"
          placeholder="Enter your reason here.."
          value={form.reason}
          onChange={(e) => setForm({ ...form, reason: e.target.value })}
        />
        {error && <p className="text-xs text-red-500 mb-3">{error}</p>}

        <div className="flex justify-end gap-3 mt-auto pt-2">
          <button
            onClick={onClose}
            className="flex-1 h-10 border border-blue-500 rounded-[8px] text-sm text-blue-600 radius-[8px] hover:bg-blue-50 font-medium"
            style={{ maxWidth: 163 }}
          >
            Cancel
          </button>
          <button
            onClick={handleDeclare}
            disabled={submitting}
            className="flex-1 h-10 rounded-[8px] text-sm text-white bg-[#4253C7] hover:bg-blue-700 font-semibold disabled:opacity-60 radius-[8px]"
            style={{ maxWidth: 163 }}
          >
            {submitting ? "Declaring..." : "Declare"}
          </button>
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
//  MANAGE VIEW
// ═══════════════════════════════════════════════════════════════════
const ManageView = ({ role }) => {
  const navigate = useNavigate();
  const isAdmin = role === "admin";
  const onBack = () => navigate("/dashboard/leave");
  const [showHolidayModal, setShowHolidayModal] = useState(false);
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [selectedIdx, setSelectedIdx] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const [filterUserId, setFilterUserId] = useState("");
  const [filterSearch, setFilterSearch] = useState("");
  const [historyPage, setHistoryPage] = useState(1);
  const [stats, setStats] = useState({ pending: 0, rejected: 0, approved: 0 });
  const [requests, setRequests] = useState([]);
  const [activity, setActivity] = useState([]);
  const [history, setHistory] = useState([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyTotalPages, setHistoryTotalPages] = useState(1);
  const [usersList, setUsersList] = useState([]);
  const pageSize = 4;

  const fetchAll = useCallback(async () => {
    try {
      const [st, rq, ac, us] = await Promise.all([
        authFetch("/api/leaves/stats").then((x) => x.json()),
        authFetch("/api/leaves/requests").then((x) => x.json()),
        authFetch("/api/leaves/activity").then((x) => x.json()),
        authFetch("/api/admin/users?picker=true").then((x) => x.json()),
      ]);
      if (st?.data) setStats(st.data);
      if (rq?.data) setRequests(rq.data);
      if (ac?.data) setActivity(ac.data);
      if (us?.data) setUsersList(us.data);
    } catch {}
  }, []);

  // ✅ Correct
  const fetchHistory = useCallback(async (page = 1, userId = "") => {
    try {
      const params = new URLSearchParams({ page, limit: pageSize });
      if (userId) params.set("userId", userId);
      const res = await authFetch(`/api/leaves/history?${params}`);
      const json = await res.json();
      if (json?.data) {
        setHistory(json.data);
        setHistoryTotal(json.pagination?.totalResults || json.data.length);
        setHistoryTotalPages(json.pagination?.totalPages || 1);
      }
    } catch {}
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);
  useEffect(() => {
    fetchHistory(historyPage, filterUserId);
  }, [historyPage, filterUserId, fetchHistory]);

  const fetchDetail = async (id) => {
    try {
      const r = await authFetch(`/api/leaves/${id}`);
      const j = await r.json();
      if (j?.data) setDetailData(j.data);
    } catch {}
  };

  const handleAction = async (id, action) => {
    try {
      const r = await authFetch(`/api/leaves/${id}/${action}`, {
        method: "PATCH",
      });
      const j = await r.json();
      if (r.ok && j?.data) {
        setRequests((prev) =>
          prev.map((x) => (x._id === id ? { ...x, status: j.data.status } : x)),
        );
        setSelectedIdx(null);
        setDetailData(null);
        fetchAll();
        fetchHistory(historyPage, filterUserId);
      }
    } catch {}
  };

  // A manager's own leave is admin-only: managers should not see or action
  // another manager's leave requests at all — only requests from employees
  // ("viewer"/members) are visible to them.
  const getRequesterRole = (r) =>
    (r?.user?.role || r?.employee?.role || r?.userRole || "")
      .toString()
      .toLowerCase();

  const pendingRequests = requests
    .filter((r) => r.status === "PENDING")
    .filter((r) => isAdmin || getRequesterRole(r) !== "manager");
  const selectedReq =
    selectedIdx !== null ? pendingRequests[selectedIdx] : null;

  const canActOnRequest = (r) => isAdmin || getRequesterRole(r) !== "manager";
  const actColor = (t) =>
    t === "leave_approved"
      ? "#22c55e"
      : t === "leave_rejected"
        ? "#ef4444"
        : t === "leave_applied"
          ? "#f59e0b"
          : t === "holiday_declared"
            ? "#8b5cf6"
            : "#4f6ef7";

  return (
    <div className="p-3 sm:p-6 min-h-screen">
      {/* Combined card: Leave Management → Recent Activity in one card,
          inner sections separated only by rounded borders */}
      <div className="bg-white rounded-2xl px-3 sm:px-5 py-4 shadow-sm mb-5 flex flex-col gap-5">
        {/* Header */}
        <div className=" flex  items-center justify-between gap-3 px-4 sm:px-6 py-4 ">
          <div className="flex flex-col gap-0.5">
            <h2 className="text-[24px] sm:text-xl font-medium text-gray-900">
              Leave Management
            </h2>
            <p className="text-xs text-gray-400 mt-0.5">
              Manage employee leave requests
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {!isAdmin && (
              <button
                onClick={onBack}
                className="border border-gray-200 text-gray-600 w-[163px] max-w-full text-sm font-medium px-3 py-2 rounded-[8px] hover:bg-gray-50"
              >
                ← Back
              </button>
            )}
            {!isAdmin && (
              <button
                onClick={() => setShowApplyModal(true)}
                className="bg-[#4253C7] text-white text-sm font-normal w-[163px] max-w-full px-4 py-2 rounded-[8px] hover:bg-blue-700"
              >
                Apply
              </button>
            )}
            <div className="relative inline-block group">
              <span className="text-[#7C7C7C] text-[22px] cursor-pointer select-none">
                ⓘ
              </span>

              <div
                className="
      absolute
      top-8
      right-0
      hidden
      group-hover:block
      w-[150px]
      rounded-[14px]
      border
      border-[#D9D9D9]
      bg-white
      shadow-[0_6px_20px_rgba(0,0,0,0.08)]
      px-4
      py-4
      z-50
    "
              >
                <p className="text-[10px] font-medium text-[#222] leading-8">
                  RL = Restricted Leave
                </p>

                <p className="text-[10px] font-medium text-[#222] leading-8">
                  CL = Casual Leave
                </p>

                <p className="text-[10px] font-medium text-[#222] leading-8">
                  ML = Medical Leave
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="inline-flex flex-nowrap gap-3 border border-gray-200 rounded-2xl p-3">
            {[
              { count: stats.pending, label: "Pending" },
              { count: stats.rejected, label: "Rejected" },
              { count: stats.approved, label: "Approved" },
            ].map((item) => (
              <div
                key={item.label}
                className="rounded-2xl bg-[#EEF0F7] flex items-center gap-2 px-4 py-3 max-w-[500px] min-w-[250px]"
              >
                <span className="text-2xl font-bold text-gray-900">
                  {item.count}
                </span>
                <span className="text-sm text-gray-500">{item.label}</span>
              </div>
            ))}
          </div>

          <button
            onClick={() => setShowHolidayModal(true)}
            className="bg-[#4253C7] text-white text-[14px] font-regular rounded-lg hover:bg-blue-700 ml-[100px] w-[163px] max-w-full px-4 py-2"
          >
            Declare Holiday
          </button>
        </div>

        {/* Grid */}
        {/* The right panel caps at 400px but is allowed to shrink: a hard
            `400px` track keeps its full width even at the lg breakpoint
            (1024px), which leaves the left column ~370px while the Leave
            Requests table inside it needs 480px — so the table overflowed.
            minmax(0, …) lets the panel give way when space is tight, and the
            minmax(0, 1fr) on the left column lets that column shrink below
            its content's intrinsic width instead of pushing the grid wider. */}
        <div
          className={`grid grid-cols-1 ${selectedIdx !== null ? "lg:grid-cols-[minmax(0,1fr)_minmax(0,384px)]" : ""} gap-5 mb-5`}
        >
          <div className="flex flex-col gap-5 ">
            {/* Leave Requests */}
            <div className="bg-white  px-2 sm:px-2 py-2 shadow-sm border border-gray-100 rounded-[8px] p-2 sm:p-4">
              <h3 className="font-medium text-[20px] text-gray-800 mb-4">
                Leave Requests
              </h3>
              {/* Caps the visible list to ~6 rows; beyond that it scrolls
                  vertically instead of growing the card indefinitely. */}
              <div className="overflow-x-auto overflow-y-auto max-h-[320px]">
                <table
                  className="w-full table-fixed text-sm border border-gray-200 rounded-lg overflow-hidden border-separate border-spacing-0"
                  style={{ minWidth: 480 }}
                >
                  <thead className="sticky top-0 z-10">
                    <tr className="text-gray-400 text-left text-xs bg-[#EEF0F7]">
                      {["Name", "From", "To", "Type", "Status", "Action"].map(
                        (h) => (
                          <th
                            key={h}
                            className="px-4 py-3 font-medium border-b border-gray-200"
                          >
                            {h}
                          </th>
                        ),
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {pendingRequests.length === 0 ? (
                      <tr>
                        <td
                          colSpan={6}
                          className="px-4 py-8 text-center text-gray-400 text-sm"
                        >
                          No pending requests
                        </td>
                      </tr>
                    ) : (
                      pendingRequests.map((r, i) => (
                        <tr key={r._id || i} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-gray-800 font-medium border-b border-gray-100">
                            {r.user?.name || r.name}
                          </td>
                          <td className="px-4 py-3 text-gray-500 border-b border-gray-100">
                            {r.from || formatShort(r.fromDate)}
                          </td>
                          <td className="px-4 py-3 text-gray-500 border-b border-gray-100">
                            {r.to || formatShort(r.toDate)}
                          </td>
                          <td className="px-4 py-3 text-gray-500 border-b border-gray-100">
                            {r.leaveType || r.type}
                          </td>
                          <td className="px-4 py-3 border-b border-gray-100">
                            <StatusBadge status={r.status} />
                          </td>
                          <td className="px-4 py-3 border-b border-gray-100">
                            <div className="flex items-center gap-2">
                              <button
                                onClick={async () => {
                                  if (selectedIdx === i) {
                                    setSelectedIdx(null);
                                    setDetailData(null);
                                    return;
                                  }
                                  setSelectedIdx(i);
                                  if (r._id) await fetchDetail(r._id);
                                }}
                                className="px-3 h-7 flex items-center justify-center rounded border border-gray-200 text-xs font-medium text-gray-600 hover:bg-gray-50 transition"
                              >
                                {selectedIdx === i ? "CLOSE" : "VIEW"}
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Recent Activity */}
            <div className="rounded-2xl border border-gray-100 px-6 py-5">
              <h3 className="font-bold text-gray-800 mb-4">Recent Activity</h3>

              <div className="flex flex-col gap-3 ">
                {activity.length === 0 ? (
                  <p className="text-xs text-gray-400">No recent activity</p>
                ) : (
                  activity.slice(0, 5).map((a, i) => {
                    const dateLabel = formatActivityDates(a);
                    return (
                      <div
                        key={a._id || i}
                        className="flex items-center justify-between gap-2 border-b pb-2"
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <span
                            className="w-2 h-2 rounded-full flex-shrink-0"
                            style={{ background: actColor(a.type) }}
                          />
                          <div className="min-w-0">
                            <span className="text-sm text-gray-700 block truncate">
                              {a.action}
                            </span>
                            {dateLabel && (
                              <span className="text-xs text-gray-500 block">
                                {dateLabel}
                              </span>
                            )}
                          </div>
                        </div>
                        <span className="text-xs text-gray-400 whitespace-nowrap flex items-center gap-1 flex-shrink-0">
                          {a.status && a.status !== "INFO" && (
                            <span
                              className="text-xs font-medium"
                              style={{ color: actColor(a.type) }}
                            >
                              {a.status.charAt(0) +
                                a.status.slice(1).toLowerCase()}
                            </span>
                          )}
                          {a.status && a.status !== "INFO" ? "· " : ""}
                          {timeAgo(a.createdAt)}
                        </span>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>

          {/* Detail Panel — only visible when a request is selected */}
          {selectedIdx !== null && (
            <div className="bg-white rounded-2xl border border-gray-100 px-5 py-5 shadow-sm">
              <div className="flex items-center gap-3 mb-4">
                {/* Outlined avatar circle, matching the design */}
                <div className="w-10 h-10 rounded-full border border-gray-300 text-gray-500 flex items-center justify-center flex-shrink-0">
                  <User className="w-5 h-5" strokeWidth={1.5} />
                </div>
                <div className="min-w-0">
                  <p className="font-bold text-gray-900 text-lg leading-tight truncate">
                    {detailData?.employee?.name ||
                      selectedReq?.user?.name ||
                      "—"}
                  </p>
                  <p className="text-xs text-gray-400 truncate">
                    {detailData?.employee?.designation ||
                      selectedReq?.user?.designation ||
                      "—"}
                  </p>
                </div>
              </div>
              <p className="text-[14px] text-black mb-1">Reason</p>
              <div className="bg-white border border-gray-200 rounded-xl p-3 text-sm text-gray-600 mb-4 min-h-[88px]">
                {selectedReq?.reason || "—"}
              </div>

              {/* Leave Summary — bordered card wrapping the two stat boxes */}
              <div className="border border-gray-200 rounded-xl p-4 mb-4">
                <h4 className="font-bold text-gray-900 text-lg mb-3">
                  Leave Summary
                </h4>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    {
                      label: "This Month",
                      value: detailData?.leaveSummary?.thisMonth ?? "—",
                    },
                    {
                      label: "This Year",
                      value: detailData?.leaveSummary?.thisYear ?? "—",
                    },
                  ].map((item) => (
                    <div
                      key={item.label}
                      className="bg-gray-50 rounded-xl p-3 text-left"
                    >
                      <p className="text-[16px] text-gray-400">{item.label}</p>
                      {/* Number and "Days" share a baseline, as in the design */}
                      <div className="flex items-baseline gap-2 mt-1">
                        <span className="text-2xl font-semibold text-gray-900">
                          {item.value}
                        </span>
                        <span className="text-[16px] text-gray-400">Days</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Remaining Leaves — bordered card wrapping the type rows */}
              <div className="border border-gray-200 rounded-xl p-4 mb-5">
                <h4 className="font-bold text-gray-900 text-lg mb-3">
                  Remaining Leaves
                </h4>
                <div className="flex flex-col">
                  {["RL", "CL", "ML"].map((type, i) => (
                    <div
                      key={type}
                      className={`flex items-center justify-between py-3 text-sm ${
                        i > 0 ? "border-t border-gray-200" : ""
                      }`}
                    >
                      <span className="text-gray-500">{type}</span>
                      <span className="text-gray-900 font-semibold">
                        {detailData?.remainingLeaves?.[type] ?? "—"} Left
                      </span>
                    </div>
                  ))}
                </div>
              </div>
              {selectedReq?.status === "PENDING" && (
                <div className="flex flex-col gap-2">
                  {!canActOnRequest(selectedReq) && (
                    <p className="text-xs text-gray-400 text-center">
                      Only an admin can approve or reject a manager's leave.
                    </p>
                  )}
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleAction(selectedReq._id, "approve")}
                      disabled={!canActOnRequest(selectedReq)}
                      className="flex-1 bg-green-500 hover:bg-green-600 text-white text-sm font-semibold rounded-[8px] py-2.5 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-green-500"
                    >
                      Accept
                    </button>
                    <button
                      onClick={() => handleAction(selectedReq._id, "reject")}
                      disabled={!canActOnRequest(selectedReq)}
                      className="flex-1 bg-red-500 hover:bg-red-600 text-white text-sm font-semibold rounded-[8px] py-2.5 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-red-500"
                    >
                      Reject
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      {/* End combined card */}

      {/* History */}
      <div className="bg-white rounded-2xl border border-gray-100 px-6 py-5 shadow-sm">
        <div className="flex items-center gap-3 mb-4">
          <h3 className="font-bold text-gray-800">Leave History</h3>
          <div className="relative">
            <button
              onClick={() => setFilterOpen((f) => !f)}
              className="flex items-center gap-1 text-xs text-gray-600 border border-gray-200 rounded-lg px-3 py-1.5"
            >
              Filter By{" "}
              {filterUserId
                ? `· ${usersList.find((u) => u._id === filterUserId)?.name || ""}`
                : ""}{" "}
              ▾
            </button>
            {filterOpen && (
              <>
                <div
                  className="fixed inset-0 z-[9]"
                  onClick={() => setFilterOpen(false)}
                />
                <div className="absolute right-0 top-9 bg-white border border-gray-100 rounded-2xl shadow-lg z-10 min-w-[220px] overflow-hidden">
                  <div className="p-2 border-b border-gray-100">
                    <input
                      autoFocus
                      type="text"
                      value={filterSearch}
                      onChange={(e) => setFilterSearch(e.target.value)}
                      placeholder="Search members..."
                      className="w-full text-xs px-3 py-2 border border-gray-200 rounded-lg outline-none focus:border-[#4253C7]"
                    />
                  </div>
                  <div className="max-h-[220px] overflow-y-auto">
                    <button
                      onClick={() => {
                        setFilterUserId("");
                        setFilterOpen(false);
                        setFilterSearch("");
                        setHistoryPage(1);
                      }}
                      className="w-full text-left px-4 py-2.5 text-xs hover:bg-gray-50 text-gray-400"
                    >
                      All employees
                    </button>
                    {usersList
                      .filter(
                        (u) =>
                          isAdmin || (u.role || "").toLowerCase() !== "manager",
                      )
                      .filter((u) =>
                        u.name
                          ?.toLowerCase()
                          .includes(filterSearch.toLowerCase()),
                      )
                      .map((u) => (
                        <button
                          key={u._id}
                          onClick={() => {
                            setFilterUserId(u._id);
                            setFilterOpen(false);
                            setFilterSearch("");
                            setHistoryPage(1);
                          }}
                          className="flex items-center gap-2 w-full text-left px-4 py-2.5 text-xs hover:bg-gray-50 text-gray-700"
                        >
                          <span className="w-6 h-6 rounded-full bg-indigo-100 text-blue-500 flex items-center justify-center text-xs font-bold">
                            {u.name?.[0]}
                          </span>
                          {u.name}
                        </button>
                      ))}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table
            className="w-full table-fixed text-sm border border-gray-200 rounded-lg overflow-hidden border-separate border-spacing-0"
            style={{ minWidth: 480 }}
          >
            <thead>
              <tr className="text-gray-400 text-left text-xs bg-[#EEF0F7]">
                {["Name", "From", "To", "Days", "Type", "Status"].map((h) => (
                  <th
                    key={h}
                    className="px-4 py-3 font-medium border-b border-gray-200"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {history.length === 0 ? (
                <tr>
                  <td
                    colSpan={6}
                    className="px-4 py-8 text-center text-gray-400"
                  >
                    No history
                  </td>
                </tr>
              ) : (
                history.map((h, i) => (
                  <tr key={h._id || i} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-gray-800 font-medium border-b border-gray-100">
                      {h.user?.name || h.name}
                    </td>
                    <td className="px-4 py-3 text-gray-600 border-b border-gray-100">
                      {h.from || formatShort(h.fromDate)}
                    </td>
                    <td className="px-4 py-3 text-gray-600 border-b border-gray-100">
                      {h.to || formatShort(h.toDate)}
                    </td>
                    <td className="px-4 py-3 text-gray-600 border-b border-gray-100">
                      {h.totalDays || h.days}
                    </td>
                    <td className="px-4 py-3 text-gray-600 border-b border-gray-100">
                      {h.leaveType || h.type}
                    </td>
                    <td className="px-4 py-3 border-b border-gray-100">
                      <StatusBadge status={h.status} />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
            <tfoot>
              <tr className="bg-[#F5F5F5]">
                <td colSpan={6} className="px-4 py-3">
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-gray-400">
                      Showing{" "}
                      {Math.min((historyPage - 1) * pageSize + 1, historyTotal)}{" "}
                      to {Math.min(historyPage * pageSize, historyTotal)} of{" "}
                      {historyTotal} Results
                    </p>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() =>
                          setHistoryPage((p) => Math.max(1, p - 1))
                        }
                        disabled={historyPage === 1}
                        className="w-[23px] h-[35px] flex items-center border border-gray-200 justify-center rounded text-gray-400 hover:bg-gray-200 disabled:opacity-30"
                      >
                        <Icons.Left />
                      </button>

                      {[...Array(historyTotalPages)].map((_, i) => (
                        <button
                          key={i}
                          onClick={() => setHistoryPage(i + 1)}
                          className="w-7 h-7 flex items-center justify-center rounded text-sm"
                          style={
                            historyPage === i + 1
                              ? { background: "#4253C7", color: "#fff" }
                              : { color: "#6b7280" }
                          }
                        >
                          {i + 1}
                        </button>
                      ))}

                      <button
                        onClick={() =>
                          setHistoryPage((p) =>
                            Math.min(historyTotalPages, p + 1),
                          )
                        }
                        disabled={historyPage === historyTotalPages}
                        className="w-[23px] h-[35px] flex items-center justify-center rounded border border-gray-200 text-gray-400 hover:bg-gray-200 disabled:opacity-30"
                      >
                        <Icons.Right />
                      </button>
                    </div>
                  </div>
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {showHolidayModal && (
        <DeclareHolidayModal
          onClose={() => setShowHolidayModal(false)}
          onDeclared={({ date, reason }) => {
            // Reflect immediately in Recent Activity
            const dateLabel = date
              ? new Date(date).toLocaleDateString("en-US", {
                  month: "short",
                  day: "2-digit",
                  year: "numeric",
                })
              : null;
            setActivity((prev) => [
              {
                _id: `holiday_${Date.now()}`,
                action: `Emergency holiday declared: ${reason}`,
                type: "holiday_declared",
                status: "INFO",
                date: dateLabel,
                createdAt: new Date().toISOString(),
              },
              ...prev,
            ]);
            // Re-sync stats/requests/activity from the backend
            fetchAll();
          }}
        />
      )}
      {showApplyModal && (
        <ApplyLeaveModal
          prefillDate=""
          onClose={() => setShowApplyModal(false)}
          onSuccess={() => {}}
        />
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
//  EMPLOYEE VIEW — default for ALL roles
// ═══════════════════════════════════════════════════════════════════
const EmployeeView = ({ role }) => {
  const navigate = useNavigate();
  const today = new Date();
  const isManager = role === "manager" || role === "admin";
  const [currentDate, setCurrentDate] = useState(new Date());
  const [showModal, setShowModal] = useState(false);
  const [prefillDate, setPrefillDate] = useState("");
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDayMon = (new Date(year, month, 1).getDay() + 6) % 7;
  const prevDays = new Date(year, month, 0).getDate();
  const maxDate = new Date(today.getFullYear(), today.getMonth() + 6, 1);

  const [balance, setBalance] = useState({
    CL: { allocated: 10, used: 0 },
    RL: { allocated: 2, used: 0 },
    ML: { allocated: 5, used: 0 },
  });
  const [summary, setSummary] = useState({
    thisMonth: 0,
    thisYear: 0,
    remaining: 0,
  });
  const [myRequests, setMyRequests] = useState([]);
  const [history, setHistory] = useState([]);
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [holidays, setHolidays] = useState([]);

  const fetchAll = useCallback(async () => {
    try {
      const [b, s, r, h, c, hol] = await Promise.all([
        authFetch("/api/leaves/balance").then((x) => x.json()),
        authFetch("/api/leaves/summary").then((x) => x.json()),
        authFetch("/api/leaves/my-requests").then((x) => x.json()),
        // High limit so every approved leave for the year comes back in one
        // page — the endpoint has no date-range filter, so the current
        // calendar year is applied client-side below.
        authFetch("/api/leaves/history?limit=200&status=APPROVED").then((x) =>
          x.json(),
        ),
        authFetch(`/api/leaves/calendar?month=${month + 1}&year=${year}`).then(
          (x) => x.json(),
        ),
        authFetch(`/api/holidays?year=${year}`)
          .then((x) => x.json())
          .catch(() => null),
      ]);
      if (b?.data) setBalance(b.data);
      if (s?.data) setSummary(s.data);
      if (r?.data) setMyRequests(r.data);
      // Leave History: every approved leave in the current year (active/
      // upcoming approved leaves stay in My Leave Requests, not here).
      if (h?.data) {
        const currentYear = new Date().getFullYear();
        setHistory(
          h.data.filter((x) => {
            if ((x.status || "").toUpperCase() !== "APPROVED") return false;
            const d = x.fromDate || x.from;
            return d && new Date(d).getFullYear() === currentYear;
          }),
        );
      }
      if (c?.data) setCalendarEvents(c.data);
      if (Array.isArray(hol?.data)) setHolidays(hol.data);
    } catch {}
  }, [month, year]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);
  // Inside ManageView, after the existing useEffect for fetchAll:
  useEffect(() => {
    const interval = setInterval(() => {
      fetchAll();
    }, 30000); // poll every 30s
    return () => clearInterval(interval);
  }, [fetchAll]);
  const getHoliday = (day) => {
    const ds = new Date(year, month, day).toDateString();
    return holidays.find((h) => new Date(h.date).toDateString() === ds) || null;
  };

  const getDateStatus = (day) => {
    const ds = new Date(year, month, day).toDateString();
    return (
      calendarEvents.find((e) => new Date(e.date).toDateString() === ds)
        ?.status || null
    );
  };

  const handleApplySuccess = (data) => {
    setMyRequests((prev) => [data, ...prev]);
    fetchAll();
  };

  return (
    <div className="p-3 sm:p-6 min-h-screen">
      {/* Combined card: Header → Calendar/Requests in one card,
          inner sections separated only by rounded borders */}
      <div className="bg-white rounded-2xl px-3 sm:px-5 py-4 shadow-sm mb-5 flex flex-col gap-5">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-3 px-4 sm:px-6 py-4">
          <div>
            <h2 className="text-lg sm:text-xl font-bold text-gray-900">
              Leave Management
            </h2>
            <p className="text-xs text-gray-400 mt-0.5">
              Apply and track your leaves
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {isManager && (
              <button
                onClick={() => navigate("/dashboard/leave/manage")}
                className="bg-[#4253C7] text-white text-sm font-semibold w-[163px] max-w-full px-4 py-2 rounded-[8px] hover:bg-blue-700"
              >
                Manage
              </button>
            )}
            <button
              onClick={() => {
                setPrefillDate("");
                setShowModal(true);
              }}
              className="bg-[#4253C7] text-white text-sm font-semibold w-[163px] max-w-full px-4 py-2 rounded-lg hover:bg-blue-700"
            >
              Apply
            </button>
            <div className="relative inline-block group">
              <span className="text-[#7C7C7C] text-[22px] cursor-pointer select-none">
                ⓘ
              </span>

              <div
                className="
      absolute
      top-8
      right-0
      hidden
      group-hover:block
      w-[150px]
      rounded-[14px]
      border
      border-[#D9D9D9]
      bg-white
      shadow-[0_6px_20px_rgba(0,0,0,0.08)]
      px-4
      py-4
      z-50
    "
              >
                <p className="text-[10px] font-medium text-[#222] leading-8">
                  RL = Restricted Leave
                </p>

                <p className="text-[10px] font-medium text-[#222] leading-8">
                  CL = Casual Leave
                </p>

                <p className="text-[10px] font-medium text-[#222] leading-8">
                  ML = Medical Leave
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Leave Balance */}
        <div className="bg-white rounded-2xl border border-gray-100 mb-5 px-6 py-6 shadow-sm w-full">
          <h3 className="font-bold text-gray-800 mb-4 text-[20px]">
            Leave Balance
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-6 w-full">
            {[
              { label: "Casual Leave", key: "CL", color: "#22C55E" },
              { label: "Restricted Leave", key: "RL", color: "#4379FE" },
              { label: "Medical Leave", key: "ML", color: "#FF0004" },
            ].map((item) => {
              const b = balance[item.key] || { allocated: 0, used: 0 };
              return (
                <div
                  key={item.key}
                  className="flex flex-col items-center justify-center rounded-2xl border border-gray-100 py-4 sm:py-5 bg-[#EEF0F7]"
                >
                  <DonutChart
                    used={b.used}
                    total={b.allocated}
                    color={item.color}
                  />
                  <p className="text-base sm:text-[20px] lg:text-[24px] font-normal text-black mt-2 sm:mt-3">
                    {item.label}
                  </p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Calendar + Right Panel */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-5">
          {/* Calendar */}
          <div className="bg-white rounded-2xl border border-gray-200 px-6 py-5 shadow-sm ">
            <div className="flex items-center mb-5">
              <button
                className="w-7 h-7 flex-shrink-0 flex items-center justify-center border border-gray-200 rounded-[5px] shadow-sm bg-white text-gray-500 hover:text-gray-700 hover:bg-gray-50 text-xl leading-none"
                onClick={() => {
                  const isPast =
                    currentDate.getFullYear() < today.getFullYear() ||
                    (currentDate.getFullYear() === today.getFullYear() &&
                      currentDate.getMonth() <= today.getMonth());
                  if (!isPast) setCurrentDate(new Date(year, month - 1, 1));
                }}
              >
                ‹
              </button>
              <span className="w-[160px] text-center text-sm font-medium text-gray-900 whitespace-nowrap">
                {currentDate.toLocaleDateString("en-US", { month: "long" })} ,{" "}
                {year}
              </span>
              <button
                className="w-7 h-7 flex-shrink-0 flex items-center justify-center border border-gray-200 rounded-[5px] shadow-sm bg-white text-gray-500 hover:text-gray-700 hover:bg-gray-50 text-xl leading-none"
                onClick={() => {
                  if (currentDate < maxDate)
                    setCurrentDate(new Date(year, month + 1, 1));
                }}
              >
                ›
              </button>
            </div>

            {/* Day headers */}
            <div className="flex flex-col border border-gray-200 rounded-2xl overflow-hidden shadow-sm">
              <div className="grid grid-cols-7 text-center bg-[#EEF0F7]">
                {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
                  <div
                    key={d}
                    className="text-sm sm:text-base text-[#6B7280] font-medium py-3 w-full"
                  >
                    {d}
                  </div>
                ))}
              </div>

              {/* Days grid */}
              <div className="grid grid-cols-7 text-center bg-white [&>div:nth-last-child(-n+7)]:border-b-0">
                {[...Array(firstDayMon)].map((_, i) => (
                  <div
                    key={`p${i}`}
                    className="h-14 sm:h-[70px] text-sm sm:text-base text-gray-300 flex items-center justify-center border-b border-gray-100"
                  >
                    {String(prevDays - firstDayMon + i + 1).padStart(2, "0")}
                  </div>
                ))}
                {[...Array(daysInMonth)].map((_, i) => {
                  const day = i + 1;
                  const dateObj = new Date(year, month, day);
                  const isToday =
                    day === today.getDate() &&
                    month === today.getMonth() &&
                    year === today.getFullYear();
                  const status = getDateStatus(day);
                  const holiday = getHoliday(day);
                  const nonWorking = isNonWorkingDay(dateObj);
                  let bgColor = "";
                  let textCls = "text-gray-800";
                  if (isToday) {
                    bgColor = "#4f6ef7";
                    textCls = "text-white font-medium";
                  } else if (status === "APPROVED") {
                    bgColor = "#D8F5DF";
                    textCls = "text-[#16A34A] font-medium";
                  } else if (status === "REJECTED") {
                    bgColor = "#F8CDCD";
                    textCls = "text-[#DC2626] font-medium";
                  } else if (status === "PENDING") {
                    bgColor = "#FBEBC4";
                    textCls = "text-[#B45309] font-medium";
                  } else if (holiday || nonWorking) {
                    // Declared holiday or non-working day — shown as Non-Working (grey)
                    bgColor = "#e5e7eb";
                    textCls = "text-gray-500";
                  }
                  return (
                    <div
                      key={day}
                      title={
                        holiday
                          ? holiday.name
                          : nonWorking
                            ? "Non-working day"
                            : undefined
                      }
                      onDoubleClick={() => {
                        if (nonWorking) return;
                        setPrefillDate(toLocalYmd(dateObj));
                        setShowModal(true);
                      }}
                      onClick={() => {
                        if (nonWorking) return;
                        setPrefillDate(toLocalYmd(dateObj));
                        setShowModal(true);
                      }}
                      className={`h-14 sm:h-[70px] flex items-center justify-center relative border-b border-gray-100 transition-colors ${
                        nonWorking
                          ? "cursor-not-allowed"
                          : "cursor-pointer hover:bg-gray-50"
                      }`}
                    >
                      <span
                        className={`w-9 h-9 flex items-center justify-center rounded-[10px] text-sm sm:text-base ${textCls}`}
                        style={bgColor ? { backgroundColor: bgColor } : {}}
                      >
                        {String(day).padStart(2, "0")}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            <p className="text-sm sm:text-base text-gray-400 mt-4">
              Double click on any date to apply leave
            </p>
            <div className="flex flex-wrap gap-5 mt-3">
              {[
                { c: "#F6DFA0", l: "Pending" },
                { c: "#22c55e", l: "Approved" },
                { c: "#ef4444", l: "Rejected" },
                { c: "#e5e7eb", l: "Non -Working", b: "1px solid #d1d5db" },
              ].map((i) => (
                <span
                  key={i.l}
                  className="flex items-center gap-2 text-sm text-gray-500"
                >
                  <span
                    className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                    style={{ background: i.c, border: i.b }}
                  />
                  {i.l}
                </span>
              ))}
            </div>
          </div>

          {/* Right Panel */}
          <div className="flex flex-col gap-5 ">
            {/* Leave Summary */}
            <div className="rounded-[8px] border border-gray-200 px-5 py-5">
              <h3 className="font-bold text-gray-800 mb-4 text-base">
                Leave Summary
              </h3>
              {/* Design size 165×155 per box. Width is a cap rather than a
                  fixed value — this panel is one half of a lg:grid-cols-2
                  layout, so three 165px boxes plus gaps (519px) do not always
                  fit; below that they shrink evenly instead of overflowing.
                  Content is left-aligned, so the grid no longer centres. */}
              <div className="grid grid-cols-3 gap-3 text-left">
                {[
                  {
                    label: "This Month",
                    value: summary.thisMonth,
                    sub: "Days taken",
                  },
                  {
                    label: "This Year",
                    value: summary.thisYear,
                    sub: "Days taken",
                  },
                  { label: "Remaining", value: summary.remaining, sub: "Days" },
                ].map((item) => (
                  <div
                    key={item.label}
                    className="bg-[#EEF0F7] rounded-xl p-4 w-full max-w-[165px] h-[155px] flex flex-col justify-center"
                  >
                    <p className="text-[16px] text-gray-400">{item.label}</p>
                    <p className="text-[40px] leading-none font-normal text-gray-900 mt-2">
                      {item.value}
                    </p>
                    <p className="text-[16px] text-gray-400 mt-2">{item.sub}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* My Leave Requests */}
            <div className="rounded-2xl border border-gray-200 px-4 sm:px-5 py-5 flex-1">
              <h3 className="font-bold text-gray-800 mb-4 text-base">
                My Leave Requests
              </h3>
              <div className="overflow-x-auto overflow-y-auto max-h-[260px]">
                <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden border-separate border-spacing-0 min-w-[360px]">
                  <thead className="sticky top-0 z-10">
                    <tr className="text-gray-400 text-left text-xs bg-[#EEF0F7]">
                      {["From", "To", "Type", "Status"].map((h) => (
                        <th
                          key={h}
                          className="px-7 py-3 font-medium border-b border-gray-200"
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {myRequests.length === 0 ? (
                      <tr>
                        <td
                          colSpan={4}
                          className="px-7 py-4 text-center text-gray-400 text-xs"
                        >
                          No active requests
                        </td>
                      </tr>
                    ) : (
                      myRequests.map((r, i) => (
                        <tr key={r._id || i} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-gray-700 text-sm border-b border-gray-100">
                            {r.from || formatShort(r.fromDate)}
                          </td>
                          <td className="px-4 py-3 text-gray-700 text-sm border-b border-gray-100">
                            {r.to || formatShort(r.toDate)}
                          </td>
                          <td className="px-4 py-3 text-gray-700 text-sm border-b border-gray-100">
                            {r.leaveType || r.type}
                          </td>
                          <td className="px-4 py-3 border-b border-gray-100">
                            <StatusBadge status={r.status} />
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* End combined card */}

      {/* Leave History */}
      <div className="bg-white rounded-2xl border border-[#EBEBEB] px-4 sm:px-6 py-5 shadow-sm">
        <h3 className="font-bold text-gray-800 mb-4 text-base">
          Leave History
        </h3>
        {/* Caps the visible list to ~6 rows; beyond that it scrolls
            vertically instead of growing the card indefinitely. */}
        <div className="overflow-x-auto overflow-y-auto max-h-[308px]">
          <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden border-separate border-spacing-0 min-w-[420px]">
            <thead className="sticky top-0 z-10">
              <tr className="text-gray-400 text-left text-xs bg-[#EEF0F7]">
                {["From", "To", "Days", "Type", "Status"].map((h) => (
                  <th
                    key={h}
                    className="px-4 py-3 font-medium border-b border-gray-200"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {history.length === 0 ? (
                <tr>
                  <td
                    colSpan={5}
                    className="px-4 py-8 text-center text-gray-400 text-sm"
                  >
                    No history
                  </td>
                </tr>
              ) : (
                history.map((h, i) => (
                  <tr key={h._id || i} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-gray-700 text-sm border-b border-gray-100">
                      {h.from || formatShort(h.fromDate)}
                    </td>
                    <td className="px-4 py-3 text-gray-700 text-sm border-b border-gray-100">
                      {h.to || formatShort(h.toDate)}
                    </td>
                    <td className="px-4 py-3 text-gray-700 text-sm border-b border-gray-100">
                      {h.totalDays || h.days}
                    </td>
                    <td className="px-4 py-3 text-gray-700 text-sm border-b border-gray-100">
                      {h.leaveType || h.type}
                    </td>
                    <td className="px-4 py-3 border-b border-gray-100">
                      <StatusBadge status={h.status} />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <ApplyLeaveModal
          prefillDate={prefillDate}
          onClose={() => setShowModal(false)}
          onSuccess={handleApplySuccess}
        />
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
//  ROOT — employee view is default for ALL roles
// ═══════════════════════════════════════════════════════════════════
const LeaveManagement = () => {
  const role = useRole();
  const isAdmin = role === "admin";
  const isManager = role === "manager" || role === "admin";
  const location = useLocation();
  const navigate = useNavigate();
  const isManageRoute = location.pathname.endsWith("/manage");

  useEffect(() => {
    if (isAdmin && !isManageRoute) {
      navigate("/dashboard/leave/manage", { replace: true });
    }
  }, [isAdmin, isManageRoute, navigate]);

  if (isManageRoute && isManager) return <ManageView role={role} />;
  if (isAdmin) return null;
  return <EmployeeView role={role} />;
};

export default LeaveManagement;
